import { Box, Grid } from '@chakra-ui/react';
import { useElementsSameSetting, useElementsScoreSettings, useReviewCreationData } from 'reducers/reviewCreationSlice';

import { Template } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Divider } from 'uiToolkitV1/Divider';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { useGetElements } from '../../Hooks/dataHooks/elementsDataHooks';
import RATING_COMPUTATION_TYPE from '../Elements/constants';
import useGoalAspect from '../Elements/hooks/useGoalAspect';
import { Detail, RenderDetails } from './RenderDetails';

const ReviewerTemplates = ({ setting }) => {
  const selfTemplateSettings = setting.elementSettings.providerTypeToTemplateMap.find(
    (e) => e.providerType === REVIEW_PROVIDER_TYPES.SELF
  );
  const managerTemplateSettings = setting.elementSettings.providerTypeToTemplateMap.find(
    (e) => e.providerType === REVIEW_PROVIDER_TYPES.MANAGER
  );
  const peerTemplateSettings = setting.elementSettings.providerTypeToTemplateMap.find(
    (e) => e.providerType === REVIEW_PROVIDER_TYPES.PEER
  );
  const upwardTemplateSettings = setting.elementSettings.providerTypeToTemplateMap.find(
    (e) => e.providerType === REVIEW_PROVIDER_TYPES.UPWARD
  );
  const dlmTemplateSettings = setting.elementSettings.providerTypeToTemplateMap.find(
    (e) => e.providerType === REVIEW_PROVIDER_TYPES.DLM
  );

  const details: Detail[] = [
    {
      key: 'Reviewer Templates',
      value: '',
      isHeader: true,
    },
  ];
  if (selfTemplateSettings) {
    details.push({
      key: 'Self template:',
      value: selfTemplateSettings?.template?.name,
    });
  }
  if (managerTemplateSettings) {
    details.push({
      key: 'Manager template:',
      value: managerTemplateSettings?.template?.name,
    });
  }
  if (dlmTemplateSettings) {
    details.push({
      key: 'Dotted Line Manager template:',
      value: dlmTemplateSettings?.template?.name,
    });
  }
  if (peerTemplateSettings) {
    details.push({
      key: 'Peer template:',
      value: peerTemplateSettings?.template?.name,
    });
  }
  if (upwardTemplateSettings) {
    details.push({
      key: 'Upward template:',
      value: upwardTemplateSettings?.template?.name,
    });
  }

  return <RenderDetails details={details} />;
};

const GoalSettings = ({ setting }) => {
  const { goalRatingComputationType, isGoalRatingAutoComputed } = useElementsScoreSettings({
    feedbackFormId: setting.feedbackFormId,
  });
  const { label: goalQuestionAspects } = useGoalAspect({ feedbackFormId: setting.feedbackFormId });

  const details: Detail[] = [
    {
      key: 'Goal Settings',
      value: '',
      isHeader: true,
    },
    {
      key: 'Use configured weightage for goals or equal weightage:',
      value:
        goalRatingComputationType === RATING_COMPUTATION_TYPE.WEIGHTED_AVERAGE
          ? 'Yes, Configured'
          : 'No, use equal weightage',
    },
    {
      key: 'Automatically calculate scores for each goal according to the completion percentage:',
      value: isGoalRatingAutoComputed ? 'Yes' : 'No',
    },
    {
      key: 'Aspects of goal questions',
      value: goalQuestionAspects,
    },
  ];

  return <RenderDetails details={details} />;
};

const SettingPanel = ({ settings, setting }) => {
  const settingSameAsFormId = useElementsSameSetting({
    feedbackFormId: setting.feedbackFormId,
  });

  // disabling this rule for now as we might need this in future
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const settingsSameAsCohort = settings.find((s) => s.feedbackFormId === settingSameAsFormId);

  return (
    <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important" borderRadius="8px" mt={1}>
      <AccordionItem2 sxProps={{ borderBottom: 'none !important', p: '12px' }}>
        <AccordionButton p="20px 24px !important">
          <TextHeader4 bold>{setting?.feedbackFormName}</TextHeader4>
          {/* {settingsSameAsCohort && (
            <Flex ml="auto">
              <TextBodyMedium mr={1}>Using same settings as</TextBodyMedium>
              <TextBodyMedium bold>{settingsSameAsCohort?.cohort?.name}</TextBodyMedium>
              <TooltipInfoV1 title="tooltip" ml={2} />
            </Flex>
          )} */}
        </AccordionButton>
        <AccordionPanel2 p={0}>
          <Box bg="white">
            <Divider borderColor="neutral.CFD7DF" mx={4} />
          </Box>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="24px 24px 28px" bgColor="white">
            <ReviewerTemplates setting={setting} />
            <GoalSettings setting={setting} />
          </Grid>
        </AccordionPanel2>
      </AccordionItem2>
    </Accordion>
  );
};

export const Elements = () => {
  const { reviewCycleId } = useReviewCreationData();
  const { data: { elements } = {}, isLoading } = useGetElements({ reviewCycleId });

  if (elements?.length === 1) {
    const setting = elements[0];

    return (
      <ExpandablePaper showLoader={isLoading}>
        <ExpandablePaperHeader title="Elements" leftIconComponent={<Template boxSize="24px" color="icon.g2" />} />
        <ExpandablePaperBody>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="16px 8px 12px">
            <ReviewerTemplates setting={setting} />
            <GoalSettings setting={setting} />
          </Grid>
        </ExpandablePaperBody>
      </ExpandablePaper>
    );
  }

  return (
    <ExpandablePaper showLoader={isLoading}>
      <ExpandablePaperHeader
        title="Elements"
        leftIconComponent={<Template boxSize="24px" color="icon.g2" />}
        borderBottom="none"
        marginBottom={0}
      />
      <ExpandablePaperBody p="0 4px 4px 4px" bgColor="neutral.EBEFF3" borderRadius="8px">
        {elements?.map((setting) => <SettingPanel settings={elements} setting={setting} />)}
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
